import React, { createContext, useContext, useEffect, useState } from 'react'
import { AgTechTourConfig, AgTechTourStep } from './AgTechTourTypes'

type AgTechTourState = {
    isActive: boolean
    config: AgTechTourConfig | null
    currentStepIndex: number
}

export type AgTechTourContextValue = {
    isActive: boolean
    currentStepIndex: number
    config: AgTechTourConfig | null
    currentStep: AgTechTourStep | undefined
    startTour: (config: AgTechTourConfig) => void
    nextStep: () => void
    prevStep: () => void
    closeTour: () => void
}

const defaultValue: AgTechTourContextValue = {
    isActive: false,
    currentStepIndex: 0,
    config: null,
    currentStep: undefined,
    startTour: () => {},
    nextStep: () => {},
    prevStep: () => {},
    closeTour: () => {},
}

// Steps flagged skipIfMissing (e.g. controls hidden by an empty state) would dim the whole screen
// with nothing highlighted, so navigation skips past them. Other steps may rely on the previous
// step's onActivate to create their target, so they are never skipped.
const findStepIndex = (config: AgTechTourConfig, from: number, direction: 1 | -1): number => {
    for (let i = from; i >= 0 && i < config.steps.length; i += direction) {
        const step = config.steps[i]
        if (!step.skipIfMissing || document.getElementById(step.targetId)) return i
    }
    return -1
}

export const AgTechTourContext = createContext<AgTechTourContextValue>(defaultValue)

export const AgTechTourProvider: React.FC<React.PropsWithChildren<{ onClosePopups?: () => void }>> = ({ children, onClosePopups }) => {
    const [state, setState] = useState<AgTechTourState>({
        isActive: false,
        config: null,
        currentStepIndex: 0,
    })

    const currentStep = state.config?.steps[state.currentStepIndex]

    useEffect(() => {
        if (state.isActive && currentStep) {
            if (currentStep.closePopupsOnActivate) {
                onClosePopups?.()
            }
            currentStep.onActivate?.()
        }
    }, [state.currentStepIndex, state.isActive])

    const startTour = (config: AgTechTourConfig) => {
        const firstIndex = findStepIndex(config, 0, 1)
        if (firstIndex < 0) return
        setState({ isActive: true, config, currentStepIndex: firstIndex })
    }

    const nextStep = () => {
        setState(prev => {
            if (!prev.config) return prev
            const nextIndex = findStepIndex(prev.config, prev.currentStepIndex + 1, 1)
            if (nextIndex < 0) {
                return { isActive: false, config: null, currentStepIndex: 0 }
            }
            return { ...prev, currentStepIndex: nextIndex }
        })
    }

    const prevStep = () => {
        setState(prev => {
            if (!prev.config) return prev
            const prevIndex = findStepIndex(prev.config, prev.currentStepIndex - 1, -1)
            return prevIndex < 0 ? prev : { ...prev, currentStepIndex: prevIndex }
        })
    }

    const closeTour = () => {
        setState({ isActive: false, config: null, currentStepIndex: 0 })
    }

    return (
        <AgTechTourContext.Provider value={{ isActive: state.isActive, currentStepIndex: state.currentStepIndex, config: state.config, currentStep, startTour, nextStep, prevStep, closeTour }}>
            {children}
        </AgTechTourContext.Provider>
    )
}

export const useAgTechTour = () => useContext(AgTechTourContext)
